// src/features/analytics/useAnalyticsMeta.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import { utcDateToSeconds } from '@/shared/utils/utcRange';
import type { AnalyticsMeta } from './types';

export interface UseAnalyticsMetaResult {
  meta: AnalyticsMeta | null;
  /**
   * UTC midnight of the product-window start T, or null when no floor applies
   * (customerStartDate stays null through all of Phase 5 — the floor is
   * dormant but wired; Phase 6 activates it via configuration alone).
   */
  productWindowFloorSeconds: number | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Fetch /v1/analytics/meta once per session (gateway-local; account-independent). */
export function useAnalyticsMeta(): UseAnalyticsMetaResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.meta(),
    queryFn: () => AnalyticsApi.getMeta(),
    staleTime: Infinity,
  });

  const meta = query.data ?? null;
  const productWindowFloorSeconds = meta?.customerStartDate
    ? utcDateToSeconds(meta.customerStartDate)
    : null;

  return {
    meta,
    productWindowFloorSeconds,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
